import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { Typography, Box, Button } from "@mui/material";
import api from "api";
import QuickFilters from "./Filters/QuickFilter";
import FirstLineContainer from "components/common/FirstLineContainer";
import { useDispatch } from "react-redux";
import { UserContext } from "context";
import { setLoading, setDialog } from "slices/appSlice";
import StyledDataGrid from "styles/styledDataGrid";
import { useNavigate } from "react-router-dom";
import useGridResizeHook, {
  calculateGridHeight,
} from "hooks/useGridResizeHook";
import { formErrorStrfromObj } from "utils/shared";
import CustomGridToolbar from "components/GridToolbar";

const filterStorageKey = "hpConfigurationFilter";

const updateLocalStorageForFilter = (filterType, value) => {
  const filters = JSON.parse(sessionStorage.getItem(filterStorageKey)) || {};
  filters[filterType] = value;
  sessionStorage.setItem(filterStorageKey, JSON.stringify(filters));
};

const HealthPlanConfiguration = () => {
  const [healthPlans, setHealthPlans] = useState([]);
  const parsedFilters = JSON.parse(sessionStorage.getItem(filterStorageKey));

  const [selectedContract, setSelectedContract] = useState(
    parsedFilters?.contract || "All"
  );
  const [selectedStatus, setSelectedStatus] = useState(
    parsedFilters?.status || "All"
  );

  const topPanelRef = useRef(null);
  const [gridHeight, setGridHeight] = useState("auto");

  const userContext = useContext(UserContext);
  // Check if role is "POET-Avalon-Admin" or skills include "Admin" (case-insensitive)
  const isAdmin = userContext
    ? userContext.groups[0] === "POET-Avalon-Admin" ||
      (userContext.POETSkill &&
        userContext.POETSkill.some((skill) => skill.toLowerCase() === "admin"))
    : false;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      dispatch(setLoading(true));
      try {
        const response = await api.healthPlanConfig.listHealthPlanConfigs();
        setHealthPlans(response.data);
      } catch (error) {
        dispatch(
          setDialog({ message: formErrorStrfromObj(error), title: "Error" })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchData();
  }, [dispatch]);

  const handleEdit = (row, editable) => {
    navigate(
      `/hp-config/edit/${row.healthPlanClientKey}/${row.healthPlanName}`,
      { state: { editable, row } }
    );
  };

  const columns = [
    { field: "healthPlanName", headerName: "Health Plan", flex: 1 },
    {
      field: "healthPlanDisplayName",
      headerName: "Health Plan Display Name",
      flex: 3,
    },
    { field: "gtmIndicator", headerName: "GTM", flex: 1 },
    { field: "pgtmIndicator", headerName: "PGTM", flex: 1 },
    { field: "rtmIndicator", headerName: "RTM", flex: 1 },
    { field: "srxIndicator", headerName: "SRX", flex: 1 },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="text"
          color="info"
          onClick={() => handleEdit(params.row, isAdmin)}
        >
          {isAdmin ? "Edit" : "View"}
        </Button>
      ),
    },
  ];

  const filteredHealthPlans = healthPlans.filter((plan) => {
    if (selectedStatus === "All" && selectedContract === "All") return true;

    let contractMatch = true;
    if (selectedStatus !== "All") {
      switch (selectedContract) {
        case "GTM":
          contractMatch = plan.gtmIndicator === selectedStatus;
          break;
        case "PGTM":
          contractMatch = plan.pgtmIndicator === selectedStatus;
          break;
        case "RTM":
          contractMatch = plan.rtmIndicator === selectedStatus;
          break;
        case "SRX":
          contractMatch = plan.srxIndicator === selectedStatus;
          break;
        case "All":
          contractMatch =
            plan.srxIndicator === selectedStatus ||
            plan.gtmIndicator === selectedStatus ||
            plan.pgtmIndicator === selectedStatus ||
            plan.rtmIndicator === selectedStatus;
          break;
        default:
          contractMatch = true;
      }
    } else {
      switch (selectedContract) {
        case "GTM":
          contractMatch = plan.gtmIndicator !== "No";
          break;
        case "PGTM":
          contractMatch = plan.pgtmIndicator !== "No";
          break;
        case "RTM":
          contractMatch = plan.rtmIndicator !== "No";
          break;
        case "SRX":
          contractMatch = plan.srxIndicator !== "No";
          break;
        default:
          contractMatch = true;
      }
    }

    return contractMatch;
  });

  const resetFilter = () => {
    sessionStorage.removeItem(filterStorageKey);
  };

  const resizeEvent = useCallback(
    () => calculateGridHeight(topPanelRef, setGridHeight),
    [setGridHeight]
  );
  useGridResizeHook(resizeEvent);

  const handleAccordionToggle = () => {
    setTimeout(() => {
      resizeEvent();
    }, 400);
  };

  if (!userContext) return null;

  return (
    <div>
      <Box ref={topPanelRef}>
        <FirstLineContainer>
          <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
            <Box component="span" fontWeight="fontWeightBold">
              Health Plan Configuration
            </Box>
          </Typography>
        </FirstLineContainer>

        <Box sx={{ paddingY: 1 }}>
          <QuickFilters
            setSelectedContract={setSelectedContract}
            setSelectedStatus={setSelectedStatus}
            selectedStatus={selectedStatus}
            selectedContract={selectedContract}
            handleAccordionToggle={handleAccordionToggle}
            updateLocalStorageForFilter={updateLocalStorageForFilter}
            resetFilter={resetFilter}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          height: `${gridHeight}px !important `,
        }}
      >
        <StyledDataGrid
          getRowId={(row) => row.healthPlanKey}
          columns={columns}
          rows={filteredHealthPlans}
          hideFooter
          rowHeight={40}
          initialState={{
            sorting: {
              sortModel: [{ field: "healthPlanName", sort: "asc" }],
            },
          }}
          slots={{
            toolbar: CustomGridToolbar,
          }}
          slotProps={{
            toolbar: {
              // no exportComponent on this page
              exportComponent: null,
            },
          }}
        />
      </Box>
    </div>
  );
};

export default HealthPlanConfiguration;
